import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useSubscription } from "@apollo/client";
import { assert, assertDefined } from "@/utils/assertions";
import { sanitizeText } from "@/utils/sanitization";
import { RateLimitPresets, checkRateLimit } from "@/utils/rateLimiter";
import { useDebounce, useOperationLock } from "@/utils/stateManagement";
import { 
  AgentStatsUpdatesDocument, 
  MyAgentStatsDocument, 
  MyAgentsDocument,
  type MyAgentsQuery,
  type UserAgentFilter
} from "@/generated/user-agent-graphql";
import {
  AlertCircle,
  BarChart,
  CheckCircle,
  Clock,
  Edit,
  ExternalLink,
  Plus,
  Shield,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import Navbar from "@/components/layout/navbar";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  // CardDescription,
  // CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Use GraphQL generated types
type Agent = NonNullable<MyAgentsQuery['myAgents']['edges'][0]['node']>;

// Valid agent statuses for state transitions (for future use)
// const AGENT_STATUS_TRANSITIONS: Record<Agent['status'], Agent['status'][]> = {
//   active: ['disabled'],
//   pending: ['active', 'rejected'],
//   rejected: ['pending'],
//   disabled: ['active'],
// };

// Performance thresholds (for future use)
// const PERFORMANCE_THRESHOLDS = {
//   successRate: {
//     excellent: 99,
//     good: 95,
//     poor: 90,
//   },
// } as const;

// Mock user ID for subscriptions - TODO: Get from auth context
const MOCK_USER_ID = "current-user-id";

export function SAManagementDashboard() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState("all");
  const [deleteAgent, setDeleteAgent] = useState<Agent | null>(null);
  const { acquireLock } = useOperationLock();
  
  // Refs for cleanup
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // GraphQL queries
  const filter: UserAgentFilter = useMemo(() => ({
    search: searchQuery || undefined,
    isInternal: selectedTab === "internal" ? true : selectedTab === "external" ? false : undefined,
    reviewStatus: selectedTab === "pending" ? "PENDING" as const : undefined,
  }), [searchQuery, selectedTab]);

  const { data: agentsData, loading, error } = useQuery(MyAgentsDocument, {
    variables: {
      filter,
      first: 50, // Get more agents for better filtering
    },
    errorPolicy: 'all',
  });

  const { data: statsData } = useQuery(MyAgentStatsDocument, {
    errorPolicy: 'all',
  });

  // Real-time stats updates subscription
  const { data: statsUpdates } = useSubscription(AgentStatsUpdatesDocument, {
    variables: { userID: MOCK_USER_ID },
    errorPolicy: 'all',
  });

  // Extract agents from GraphQL connection structure
  const agents: Agent[] = useMemo(() => {
    if (!agentsData?.myAgents?.edges) return [];
    return agentsData.myAgents.edges.map(edge => edge.node);
  }, [agentsData?.myAgents?.edges]);

  // Apply client-side filtering (GraphQL already handles most filtering)
  const filteredAgents = useMemo(() => {
    if (!agents.length) return [];
    
    const sanitizedQuery = sanitizeText(searchQuery).toLowerCase();
    
    return agents.filter((agent: Agent) => {
      // Pre-condition: agent must have required fields
      assertDefined(agent.id, 'Agent ID');
      assertDefined(agent.name, 'Agent name');
      assertDefined(agent.status, 'Agent status');
      
      // Additional client-side search filtering
      if (searchQuery && !filter.search) {
        const matchesSearch =
          agent.name.toLowerCase().includes(sanitizedQuery) ||
          (agent.description && agent.description.toLowerCase().includes(sanitizedQuery));
        
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [agents, searchQuery, filter.search]);

  const getStatusBadge = useCallback((status: Agent["status"]) => {
    switch (status) {
      case "ACTIVE":
        return (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
            <CheckCircle className="mr-1 h-3 w-3" />
            Active
          </Badge>
        );
      case "INACTIVE":
        return (
          <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100">
            <AlertCircle className="mr-1 h-3 w-3" />
            Inactive
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100">
            <AlertCircle className="mr-1 h-3 w-3" />
            {status}
          </Badge>
        );
    }
  }, []);

  const getReviewStatusBadge = useCallback((reviewStatus: Agent["reviewStatus"]) => {
    switch (reviewStatus) {
      case "PENDING":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
            <Clock className="mr-1 h-3 w-3" />
            Pending Review
          </Badge>
        );
      case "APPROVED":
        return (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
            <CheckCircle className="mr-1 h-3 w-3" />
            Approved
          </Badge>
        );
      case "REJECTED":
        return (
          <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100">
            <XCircle className="mr-1 h-3 w-3" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100">
            <AlertCircle className="mr-1 h-3 w-3" />
            {reviewStatus}
          </Badge>
        );
    }
  }, []);

  const handleDeleteAgent = async () => {
    if (!deleteAgent) return;
    
    // Check rate limit
    const userId = 'current-user'; // TODO: Get from auth context
    if (!checkRateLimit(userId, 'delete-agent', RateLimitPresets.API_CALLS)) {
      toast.error('Too many delete attempts. Please wait.');
      return;
    }

    try {
      await acquireLock(`delete-agent-${deleteAgent.id}`, async () => {
        // Pre-condition: Agent must exist and be deletable
        assertDefined(deleteAgent.id, 'Agent ID');
        assert(
          deleteAgent.reviewStatus !== 'PENDING',
          'Cannot delete agents pending review'
        );
        
        // TODO: Execute delete mutation
        // await deleteAgentMutation({ variables: { id: deleteAgent.id } });
        console.log('Deleting agent:', deleteAgent.id);
        
        // Post-condition: Agent should be removed from list
        if (isMountedRef.current) {
          setDeleteAgent(null);
          toast.success('Agent deleted successfully');
        }
      });
    } catch (error) {
      console.error('Error deleting agent:', error);
      if (error instanceof Error) {
        toast.error(`Failed to delete agent: ${error.message}`);
      } else {
        toast.error('Failed to delete agent. Please try again.');
      }
    }
  };

  // Use real-time stats from subscription or fallback to query data
  const currentStats = useMemo(() => {
    const realTimeStats = statsUpdates?.agentStatsUpdates;
    const queryStats = statsData?.myAgentStats;
    
    if (realTimeStats) {
      return realTimeStats;
    } else if (queryStats) {
      return queryStats;
    }
    
    // Fallback: calculate from agents data
    if (!agents.length) {
      return {
        totalAgents: 0,
        activeAgents: 0,
        pendingAgents: 0,
        totalUsage: 0,
        averageSuccessRate: 0,
        monthToDateRevenue: 0,
        revenueGrowthPercent: 0,
      };
    }
    
    const totalUsage = agents.reduce((sum, agent) => {
      // Invariant: usage count must be non-negative
      assert(agent.usageCount >= 0, 'Usage count must be non-negative');
      return sum + agent.usageCount;
    }, 0);
    
    const activeAgents = agents.filter(a => a.status === "ACTIVE");
    const avgSuccessRate = activeAgents.length > 0
      ? activeAgents.reduce((sum, a) => sum + a.successRate, 0) / activeAgents.length
      : 0;
    
    return {
      totalAgents: agents.length,
      activeAgents: activeAgents.length,
      pendingAgents: agents.filter(a => a.reviewStatus === "PENDING").length,
      totalUsage,
      averageSuccessRate: avgSuccessRate,
      monthToDateRevenue: 0, // TODO: Calculate from real data
      revenueGrowthPercent: 0, // TODO: Calculate from real data
    };
  }, [agents, statsData?.myAgentStats, statsUpdates?.agentStatsUpdates]);
  
  // Debounced search to prevent excessive filtering
  const debouncedSetSearchQuery = useDebounce((value: string) => {
    setSearchQuery(value);
  }, 300);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container mx-auto py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SA Management Dashboard</h1>
          <p className="mt-2 text-muted-foreground">
            Manage your specialized agents and track their performance
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate("/sa/create-internal")}
            variant="outline"
          >
            <Plus className="mr-2 h-4 w-4" />
            Internal SA
          </Button>
          <Button onClick={() => navigate("/sa/submit-external")}>
            <Plus className="mr-2 h-4 w-4" />
            Submit External SA
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="mb-8 grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currentStats.totalAgents}</div>
            <p className="text-xs text-muted-foreground">
              {currentStats.activeAgents} active, {currentStats.pendingAgents} pending
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Usage</CardTitle>
            <BarChart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {currentStats.totalUsage.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">All-time invocations</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Success Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {currentStats.averageSuccessRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">Average across all agents</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenue (MTD)</CardTitle>
            <span className="text-muted-foreground">$</span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${currentStats.monthToDateRevenue.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">
              {currentStats.revenueGrowthPercent > 0 ? '+' : ''}{currentStats.revenueGrowthPercent.toFixed(1)}% from last month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Your Specialized Agents</CardTitle>
            <Input
              placeholder="Search agents..."
              defaultValue={searchQuery}
              onChange={(e) => debouncedSetSearchQuery(e.target.value)}
              className="max-w-sm"
              maxLength={100}
            />
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="all">All Agents</TabsTrigger>
              <TabsTrigger value="internal">Internal</TabsTrigger>
              <TabsTrigger value="external">External</TabsTrigger>
              <TabsTrigger value="pending">Pending Review</TabsTrigger>
            </TabsList>

            <TabsContent value={selectedTab}>
              {loading ? (
                <div className="flex h-32 items-center justify-center">
                  <p className="text-muted-foreground">Loading agents...</p>
                </div>
              ) : error ? (
                <div className="flex h-32 items-center justify-center">
                  <p className="text-red-600">Error loading agents: {error.message}</p>
                </div>
              ) : filteredAgents?.length === 0 ? (
                <div className="flex h-32 flex-col items-center justify-center">
                  <p className="text-muted-foreground">No agents found</p>
                  <Button
                    variant="link"
                    onClick={() => navigate("/sa/submit-external")}
                    className="mt-2"
                  >
                    Submit your first agent
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Usage</TableHead>
                      <TableHead>Success Rate</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAgents?.map((agent: Agent) => (
                      <TableRow key={agent.id}>
                        <TableCell>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {sanitizeText(agent.name)}
                              </span>
                              {agent.isInternal && (
                                <Badge variant="secondary" className="text-xs">
                                  Internal
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {sanitizeText(agent.description || "")}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {getStatusBadge(agent.status)}
                            {getReviewStatusBadge(agent.reviewStatus)}
                          </div>
                        </TableCell>
                        <TableCell>{agent.usageCount.toLocaleString()}</TableCell>
                        <TableCell>
                          {agent.successRate > 0 ? `${agent.successRate}%` : "-"}
                        </TableCell>
                        <TableCell>
                          {new Date(agent.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                •••
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate(`/sa/${agent.id}`)
                                }
                              >
                                <ExternalLink className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate(`/sa/${agent.id}/analytics`)
                                }
                              >
                                <BarChart className="mr-2 h-4 w-4" />
                                View Analytics
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate(`/sa/${agent.id}/edit`)
                                }
                                disabled={agent.reviewStatus === "PENDING"}
                              >
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Agent
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setDeleteAgent(agent)}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Agent
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteAgent}
        onOpenChange={(open) => !open && setDeleteAgent(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Agent</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteAgent?.name}"? This action
              cannot be undone and will remove all associated data and analytics.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAgent(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAgent}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}